/**
 * Single task row — used across My Day buckets.
 */
import React, { useState } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { CheckCircle2, Clock, MoreVertical, Phone, Users2, FileText, AlertCircle, PlayCircle } from 'lucide-react';

const PRIORITY_STYLE = {
  urgent: 'bg-red-500/15 text-red-600 border-red-500/30',
  high: 'bg-orange-500/15 text-orange-600 border-orange-500/30',
  normal: 'bg-blue-500/15 text-blue-600 border-blue-500/30',
  low: 'bg-slate-400/15 text-slate-500 border-slate-400/30',
};

const TYPE_ICON = {
  call: Phone, follow_up_call: Phone, demo_schedule: Users2, site_visit: Users2,
  visit_merchant: Users2, document_request: FileText, document_review: FileText,
};

function formatDue(dueDate) {
  if (!dueDate) return 'No due date';
  const d = new Date(dueDate);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (isToday) return `Today, ${time}`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + `, ${time}`;
}

export function TaskCard({ task, onComplete, onSnooze, isOverdue, onReactivate }) {
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const Icon = TYPE_ICON[task.task_type] || Clock;

  return (
    <div
      className="flex items-start gap-3 rounded-xl border border-border bg-card p-3.5 hover:border-primary/40 transition-colors"
      data-testid={`task-card-${task.id}`}
    >
      <button
        onClick={() => onComplete(task)}
        className="mt-0.5 shrink-0 rounded-full border-2 border-muted-foreground/30 h-5 w-5 hover:border-emerald-500 hover:bg-emerald-500/10 transition-colors"
        data-testid={`task-complete-toggle-${task.id}`}
        aria-label="Complete task"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium leading-tight">{task.title}</p>
          <Badge variant="outline" className={PRIORITY_STYLE[task.priority] || PRIORITY_STYLE.normal}>
            {task.priority}
          </Badge>
          {task.auto_created && (
            <Badge variant="outline" className="text-[10px] bg-violet-500/10 text-violet-600 border-violet-500/30">auto</Badge>
          )}
        </div>
        {task.subject_name && (
          <p className="text-xs text-muted-foreground mt-0.5">{task.subject_name}{task.contact_name ? ` · ${task.contact_name}` : ''}</p>
        )}
        <div className="flex items-center gap-1.5 mt-1.5 text-xs text-muted-foreground">
          <Icon className="h-3 w-3" />
          <span className={isOverdue ? 'text-red-500 font-medium flex items-center gap-1' : ''}>
            {isOverdue && <AlertCircle className="h-3 w-3" />}
            {task.status === 'snoozed'
              ? (task.due_date ? `Waiting until ${formatDue(task.due_date)}` : 'Waiting for merchant response')
              : formatDue(task.due_date)}
          </span>
        </div>
        {task.status === 'snoozed' && task.snooze_reason && (
          <p className="text-xs text-muted-foreground italic mt-0.5">"{task.snooze_reason}"</p>
        )}
      </div>
      <DropdownMenu open={snoozeOpen} onOpenChange={setSnoozeOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" data-testid={`task-menu-${task.id}`}>
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {onReactivate && (
            <DropdownMenuItem onClick={() => onReactivate(task)} data-testid={`task-reactivate-${task.id}`}>
              <PlayCircle className="h-4 w-4 mr-2" /> Reactivate now
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => onComplete(task)} data-testid={`task-menu-complete-${task.id}`}>
            <CheckCircle2 className="h-4 w-4 mr-2" /> Complete
          </DropdownMenuItem>
          {['later_today', 'tomorrow', '2_days', 'next_week', 'when_responds'].map((opt) => (
            <DropdownMenuItem key={opt} onClick={() => onSnooze(task, opt)} data-testid={`task-snooze-${opt}-${task.id}`}>
              Snooze — {opt.replace(/_/g, ' ')}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
